/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react"
import { withMyTheme } from "../theme/theme"
import { MyButton } from "./button/MyButton"
import { useTranslation } from "react-i18next"
import { Navigation, Restaurant, Hotel, Celebration, TableBar, FormatListNumbered, Menu, Close, Person, LocationOn, Memory, QuestionAnswer, Event } from "@mui/icons-material"
import { IconButton } from "@mui/material"
import { LOCATION_ID, navigateToMap } from "../landing/location/Location"
import { SCHEDULE_ID } from "../landing/schedule/Schedule"
import { TABLES_ID } from "../landing/tables/Tables"
import { MENU_ID } from "../landing/menu/Menu"
import { ATTRACTIONS_ID } from "../landing/attractions/Attractions"
import { useState, useEffect } from "react"
import { ACCOMODATION_ID } from "../landing/accomodation/Accomodation"
import i18next from "i18next"
import { scrollSmoothly } from "../util/scrollSmoothly"
import { MainConfig } from "../../mainConfig/configHelper"
import { WITNESSES_ID } from "../landing/weddingWitnesses/WeddingWitnesses"
import { MEMORIES_ID } from "../landing/memories/Memories"
import { FAQ_ID } from "../landing/faq/Faq"
import { RSVP_ID } from "../landing/rsvp/Rsvp"

interface NavButton {
    textId: string;
    icon: React.ReactNode;
    onClick: () => void;
}

const buttons: NavButton[] = [
    {
        textId: 'navigate',
        icon: <Navigation />,
        onClick: navigateToMap
    },
    {
        textId: 'location.title',
        icon: <LocationOn />,
        onClick: () => scrollSmoothly(LOCATION_ID)
    },
    {
        textId: 'accomodation.title',
        icon: <Hotel />,
        onClick: () => scrollSmoothly(ACCOMODATION_ID)
    },
        MainConfig.menu.enabled ? {
        textId: 'menu.title',
        icon: <Restaurant />,
        onClick: () => scrollSmoothly(MENU_ID)
    } : null,
    {
        textId: 'schedule.title',
        icon: <FormatListNumbered />,
        onClick: () => scrollSmoothly(SCHEDULE_ID)
    },
        {
        textId: 'tables.title',
        icon: <TableBar />,
        onClick: () => scrollSmoothly(TABLES_ID)
    },
    {
        textId: 'attractions.title',
        icon: <Celebration />,
        onClick: () => scrollSmoothly(ATTRACTIONS_ID)
    },
    MainConfig.witnesses.enabled ? {
        textId: 'witnesses.title',
        icon: <Person />,
        onClick: () => scrollSmoothly(WITNESSES_ID)
    } : null,
    {
        textId: 'memories.title',
        icon: <Memory />,
        onClick: () => scrollSmoothly(MEMORIES_ID)
    },
    {
        textId: 'faq.title',
        icon: <QuestionAnswer />,
        onClick: () => scrollSmoothly(FAQ_ID)
    },
    {
        textId: 'rsvp.title',
        icon: <Event />,
        onClick: () => scrollSmoothly(RSVP_ID)
    }
].filter(Boolean) as NavButton[]

const FloatingButtonsContainerStyle = withMyTheme(() => css`
    position: fixed;
    bottom: 12vh;
    right: 4vh;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 1000;
    transition: all 0.3s ease-in-out;
    opacity: 0;
    transform: translateY(20px);
    pointer-events: none;
    
    &.visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: all;
    }
`)

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(20px);
    }
`

const HamburgerButtonStyle = withMyTheme((theme) => css`
    position: fixed;
    bottom: 4vh;
    right: 4vh;
    width: 7vh;
    height: 7vh;
    border-radius: 50%;
    background-color: ${theme.palette.primary.main};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1001;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    
    &:hover {
        background-color: ${theme.palette.primary.dark};
        transform: scale(1.05);
    }
    
    &.open {
        background-color: ${theme.palette.secondary.main};
    }
`)

const FloatingButtonStyle = (index: number, isClosing: boolean) => withMyTheme(() => css`
    animation: ${isClosing ? fadeOut : fadeIn} 0.3s ease-out forwards;
    animation-delay: ${isClosing ? 0.05 * index : 0.05 * (buttons.length - index)}s;
    opacity: ${isClosing ? 1 : 0};
    font-size: 2vh;
    padding: 0.8rem 1.2rem;
    border-radius: 10vh;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 120px;
`)

const HamburgerButtonIconStyle = withMyTheme(() => css`
    font-size: 3vh;
`)

export const FloatingButtons = () => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const [animationKey, setAnimationKey] = useState(0)
    
    const toggleMenu = () => {
        if (isOpen) {
            // Start closing animation
            setIsClosing(true)
            // Delay actual closing to allow animation to complete
            setTimeout(() => {
                setIsOpen(false)
                setIsClosing(false)
            }, buttons.length * 80 + 300) // Total animation time
        } else {
            // Increment the animation key to force re-render and restart animations
            setAnimationKey(prev => prev + 1)
            setIsOpen(true)
        }
    }

    return (
        <>
            <IconButton 
                css={[HamburgerButtonStyle, isOpen && css`&.open`]} 
                onClick={toggleMenu} 
                aria-label="Toggle navigation menu"
            >
                {isOpen ? <Close css={HamburgerButtonIconStyle}/> : <Menu css={HamburgerButtonIconStyle}/>}
            </IconButton>
            
            <div css={FloatingButtonsContainerStyle} className={(isOpen || isClosing) ? 'visible' : ''}>

                {(isOpen || isClosing) && buttons.map((button, index) => (
                    <MyButton
                        key={`${index}-${animationKey}`}
                        text={t(button.textId)}
                        variant="contained"
                        colorVariant="primary"
                        startIcon={button.icon}
                        onClick={button.onClick}
                        additionalCss={FloatingButtonStyle(index, isClosing)}
                    />
                ))}
            </div>
        </>
    )
}